import { useId, type ReactNode } from 'react';
import { CalendarDaysIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface AppointmentsModalFrameProps {
    title: string;
    /** The practice / virtual switch, shown next to the title. */
    viewToggle?: ReactNode;
    search: string;
    onSearchChange: (value: string) => void;
    searchPlaceholder: string;
    onClose: () => void;
    children: ReactNode;
}

// Overlay, panel and header shared by the practice and virtual appointments modals.
export function AppointmentsModalFrame({
    title,
    viewToggle,
    search,
    onSearchChange,
    searchPlaceholder,
    onClose,
    children
}: AppointmentsModalFrameProps) {
    const titleId = useId();
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black bg-opacity-50">
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="w-full max-w-[1240px] h-[85vh] max-h-[860px] flex flex-col rounded-xl border border-gray-200 bg-white shadow-2xl overflow-hidden"
            >
                <div className="flex items-center justify-between gap-4 px-5 h-[60px] border-b border-gray-200 shrink-0">
                    <div className="flex items-center gap-4 min-w-0">
                        <div className="flex items-center gap-2.5 shrink-0">
                            <span className="text-gray-400">
                                <CalendarDaysIcon className="h-[18px] w-[18px]" />
                            </span>
                            <h2 id={titleId} className="text-[15px] font-semibold text-gray-800">
                                {title}
                            </h2>
                        </div>
                        {viewToggle}
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="relative w-60">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                <MagnifyingGlassIcon className="h-[15px] w-[15px]" />
                            </span>
                            <input
                                value={search}
                                onChange={(e) => onSearchChange(e.target.value)}
                                placeholder={searchPlaceholder}
                                aria-label={searchPlaceholder}
                                className="w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 py-2 text-[13px] placeholder-gray-400 focus:outline-none focus:border-gray-400"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close"
                            className="h-9 w-9 grid place-items-center rounded-lg text-gray-400 hover:bg-gray-50 hover:text-gray-800"
                        >
                            <XMarkIcon className="h-[17px] w-[17px]" />
                        </button>
                    </div>
                </div>
                {children}
            </div>
        </div>
    );
}
